import isEmpty from 'lodash/isEmpty';
// eslint-disable-next-line import/no-cycle
import { clearCurrentUser, fetchCurrentUser, refreshCurrentUser } from './user.duck';
import {
  createUserWithIdp,
  verifyRecaptcha,
  sendOtpCode as apiSendOtpCode,
  verifyOtpCode as apiVerifyOtpCode,
  loginWithOtp as apiLoginWithOtp,
  signupWithOtp as apiSignupWithOtp,
  syncSignupPermissions as apiSyncSignupPermissions,
} from '../util/api';
import { storableError, isKnownSignupError } from '../util/errors';
import affiliate, {
  AP,
  AID,
  MEDIUM,
  CAMPAIGN,
  SOURCE,
  GCLID,
  FBCLID,
  GBRAID,
  WBRAID,
} from '../util/affiliate';
import { defaultCommission } from '../marketplace-custom-config';
import * as log from '../util/log';
import { createCommonPasswordValidationError, isCommonPassword } from '../util/passwordPolicy';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

const createDate = () => {
  const d = new Date(Date.now()); // wrapped for simpler mocking :-/
  return {
    timestamp: d.toISOString(),
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hours: d.getHours(),
    minutes: d.getMinutes(),
    seconds: d.getSeconds(),
    milliseconds: d.getMilliseconds(),
  };
};

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/Auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/Auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/Auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/Auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/Auth/LOGIN_ERROR';
export const LOGIN_WITH_IDENTITY_SUCCESS = 'app/Auth/LOGIN_WITH_IDENTITY_SUCCESS';

export const LOGOUT_REQUEST = 'app/Auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/Auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/Auth/LOGOUT_ERROR';

export const SIGNUP_STARTED = 'app/Auth/SIGNUP_STARTED';
export const SIGNUP_REQUEST = 'app/Auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/Auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/Auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/Auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/Auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/Auth/CONFIRM_ERROR';

export const SEND_OTP_CODE_REQUEST = 'app/Auth/SEND_OTP_CODE_REQUEST';
export const SEND_OTP_CODE_SUCCESS = 'app/Auth/SEND_OTP_CODE_SUCCESS';
export const SEND_OTP_CODE_ERROR = 'app/Auth/SEND_OTP_CODE_ERROR';

export const VERIFY_OTP_CODE_REQUEST = 'app/Auth/VERIFY_OTP_CODE_REQUEST';
export const VERIFY_OTP_CODE_SUCCESS = 'app/Auth/VERIFY_OTP_CODE_SUCCESS';
export const VERIFY_OTP_CODE_ERROR = 'app/Auth/VERIFY_OTP_CODE_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // otp
  sendOtpCodeError: null,
  sendOtpCodeInProgress: false,
  verifyOtpCodeError: null,
  verifyOtpCodeInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case SEND_OTP_CODE_REQUEST:
      return {
        ...state,
        sendOtpCodeInProgress: true,
        sendOtpCodeError: null,
      };
    case SEND_OTP_CODE_SUCCESS:
      return { ...state, sendOtpCodeInProgress: false };
    case SEND_OTP_CODE_ERROR:
      return { ...state, sendOtpCodeInProgress: false, sendOtpCodeError: payload };

    case VERIFY_OTP_CODE_REQUEST:
      return {
        ...state,
        verifyOtpCodeInProgress: true,
        verifyOtpCodeError: null,
      };
    case VERIFY_OTP_CODE_SUCCESS:
      return { ...state, verifyOtpCodeInProgress: false };
    case VERIFY_OTP_CODE_ERROR:
      return { ...state, verifyOtpCodeInProgress: false, verifyOtpCodeError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.Auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
// meta.loginMethod ('password' | 'otp') is only read by tracking.
export const loginWithIdentitySuccess = (payload, loginMethod) => ({
  type: LOGIN_WITH_IDENTITY_SUCCESS,
  payload,
  ...(loginMethod ? { meta: { loginMethod } } : {}),
});
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupStarted = payload => ({ type: SIGNUP_STARTED, payload });
export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = (payload, signupAuthMethod) => ({
  type: SIGNUP_SUCCESS,
  payload,
  ...(signupAuthMethod ? { meta: { signupAuthMethod } } : {}),
});
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = payload => ({ type: CONFIRM_SUCCESS, payload });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const sendOtpCodeRequest = () => ({ type: SEND_OTP_CODE_REQUEST });
export const sendOtpCodeSuccess = () => ({ type: SEND_OTP_CODE_SUCCESS });
export const sendOtpCodeError = error => ({
  type: SEND_OTP_CODE_ERROR,
  payload: error,
  error: true,
});

export const verifyOtpCodeRequest = () => ({ type: VERIFY_OTP_CODE_REQUEST });
export const verifyOtpCodeSuccess = payload => ({
  type: VERIFY_OTP_CODE_SUCCESS,
  payload,
});
export const verifyOtpCodeError = error => ({
  type: VERIFY_OTP_CODE_ERROR,
  payload: error,
  error: true,
});

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(() => dispatch(fetchCurrentUser()))
    .then(() =>
      dispatch(
        loginWithIdentitySuccess(getState().user ? getState().user.currentUser : {}, 'password')
      )
    )
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }

  if (isCommonPassword(params.password)) {
    dispatch(signupError(createCommonPasswordValidationError()));
    return Promise.resolve();
  }

  dispatch(signupRequest());

  const {
    email,
    password,
    firstName,
    lastName,
    usState,
    smsConsent,
    preferredHuntingSpecies = [],
    preferredHuntingStates = [],
    token,
    favoriteListings = [],
    favoritePackages = [],
    interestedActivities = [],
    ...rest
  } = params;

  // Build attribution data from captured landing page params
  // Don't invent fake campaigns - auth method ≠ acquisition campaign
  const attributionData = {
    // Only include UTM params if they were actually captured
    ...(affiliate.has(MEDIUM) && { aff_UTM_medium: affiliate.get(MEDIUM) }),
    ...(affiliate.has(CAMPAIGN) && { aff_UTM_campaign: affiliate.get(CAMPAIGN) }),
    ...(affiliate.has(SOURCE) && { aff_UTM_source: affiliate.get(SOURCE) }),
    ...(affiliate.has(AP) && { affiliate_program: affiliate.get(AP) }),
    ...(affiliate.has(AID) && { affiliate_id: affiliate.get(AID) }),
    // Include click IDs when present
    ...(affiliate.has(GCLID) && { gclid: affiliate.get(GCLID) }),
    ...(affiliate.has(FBCLID) && { fbclid: affiliate.get(FBCLID) }),
    ...(affiliate.has(GBRAID) && { gbraid: affiliate.get(GBRAID) }),
    ...(affiliate.has(WBRAID) && { wbraid: affiliate.get(WBRAID) }),
    // Track authentication method separately from acquisition channel
    signup_auth_method: 'email',
  };

  const createUserParams = isEmpty(rest)
    ? { email, password, firstName, lastName }
    : {
        email,
        password,
        firstName,
        lastName,
        protectedData: {
          ...attributionData,
          smsConsent,
          ...rest,
        },
        publicData: {
          usState,
          preferredHuntingSpecies,
          preferredHuntingStates: preferredHuntingStates.map(item => {
            return { address: item.address };
          }),
          interestedActivities,
          favoritePackages,
          favoriteListings,
          landownerRelationship: defaultCommission.relationship,
          joinDate: createDate(),
        },
        privateData: {
          commission: defaultCommission,
        },
      };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return verifyRecaptcha({ token })
    .then(() => {
      return sdk.currentUser
        .create(createUserParams, { expand: true })
        .then(res => dispatch(signupSuccess(res.data.data)))
        .then(() => dispatch(login(email, password)))
        .then(() =>
          apiSyncSignupPermissions().catch(error => {
            log.error(error, 'sync-signup-permissions-failed', {
              email,
            });
          })
        );
    })
    .catch(e => {
      const storable = storableError(e);
      dispatch(signupError(storable));
      if (!isKnownSignupError(storable)) {
        log.error(e, 'signup-failed', {
          email: params.email,
          firstName: params.firstName,
          lastName: params.lastName,
        });
      }
    });
};

// eslint-disable-next-line no-unused-vars
export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());

  const {
    idpId,
    idpToken,
    smsConsent,
    preferredHuntingStates = [],
    preferredHuntingSpecies = [],
    favoriteListings = [],
    favoritePackages = [],
    interestedActivities = [],
    usState,
    protectedData,
    // Attribution params forwarded from OAuth state (via st-authinfo cookie)
    utm_source,
    utm_medium,
    utm_campaign,
    gclid,
    fbclid,
    gbraid,
    wbraid,
    ap,
    aid,
  } = params;
  const { method, phoneNumber, phoneNumberFormatted, ...rest } = protectedData;

  // Build attribution data from landing page params
  // Priority: OAuth state (most reliable) > affiliate cookie > omit if unknown
  // Don't invent fake campaigns - auth method ≠ acquisition campaign
  // Note: ap/aid are included here even though they're "independent" signals
  // in affiliate.js's last-click model (see affiliate.js) - a signup should
  // still record an affiliate/partner referral even if it arrived with no
  // UTMs at all.
  const hasOAuthAttribution =
    utm_source || utm_medium || utm_campaign || gclid || fbclid || gbraid || wbraid || ap || aid;
  const hasCookieAttribution =
    affiliate.has(MEDIUM) ||
    affiliate.has(CAMPAIGN) ||
    affiliate.has(SOURCE) ||
    affiliate.has(GCLID) ||
    affiliate.has(FBCLID) ||
    affiliate.has(GBRAID) ||
    affiliate.has(WBRAID) ||
    affiliate.has(AP) ||
    affiliate.has(AID);

  let attributionData = {};

  if (hasOAuthAttribution) {
    // Use OAuth state attribution (forwarded from landing page URL)
    attributionData = {
      ...(utm_medium && { aff_UTM_medium: utm_medium }),
      ...(utm_campaign && { aff_UTM_campaign: utm_campaign }),
      ...(utm_source && { aff_UTM_source: utm_source }),
      ...(ap && { affiliate_program: ap }),
      ...(aid && { affiliate_id: aid }),
      ...(gclid && { gclid }),
      ...(fbclid && { fbclid }),
      ...(gbraid && { gbraid }),
      ...(wbraid && { wbraid }),
    };
  } else if (hasCookieAttribution) {
    // Use affiliate cookie values (captured on landing)
    attributionData = {
      ...(affiliate.has(MEDIUM) && { aff_UTM_medium: affiliate.get(MEDIUM) }),
      ...(affiliate.has(CAMPAIGN) && { aff_UTM_campaign: affiliate.get(CAMPAIGN) }),
      ...(affiliate.has(SOURCE) && { aff_UTM_source: affiliate.get(SOURCE) }),
      ...(affiliate.has(AP) && { affiliate_program: affiliate.get(AP) }),
      ...(affiliate.has(AID) && { affiliate_id: affiliate.get(AID) }),
      ...(affiliate.has(GCLID) && { gclid: affiliate.get(GCLID) }),
      ...(affiliate.has(FBCLID) && { fbclid: affiliate.get(FBCLID) }),
      ...(affiliate.has(GBRAID) && { gbraid: affiliate.get(GBRAID) }),
      ...(affiliate.has(WBRAID) && { wbraid: affiliate.get(WBRAID) }),
    };
  }
  // else: No attribution captured - leave fields omitted (don't invent fake campaigns)

  // Track authentication method separately from acquisition channel
  attributionData.signup_auth_method = method || idpId || 'oauth';

  const createUserParams = {
    idpId,
    idpToken,
    protectedData: {
      phoneNumber,
      phoneNumberFormatted,
      ...attributionData,
      smsConsent,
      ...rest,
    },
    publicData: {
      usState,
      preferredHuntingSpecies,
      preferredHuntingStates: preferredHuntingStates.map(item => {
        return { address: item.address };
      }),
      interestedActivities,
      favoriteListings,
      favoritePackages,
      landownerRelationship: defaultCommission.relationship,
      joinDate: createDate(),
    },
    privateData: {
      commission: defaultCommission,
    },
  };

  return createUserWithIdp(createUserParams)
    .then(res => {
      return dispatch(confirmSuccess(res.data.data));
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { createUserParams });
      return dispatch(confirmError(storableError(e)));
    });
};

// Send code - accepts optional context for chat session tracking
export const sendOtpCode = (email, context = null) => async dispatch => {
  dispatch(sendOtpCodeRequest());

  try {
    await apiSendOtpCode(email, context);
    dispatch(sendOtpCodeSuccess());
  } catch (error) {
    dispatch(sendOtpCodeError(storableError(error)));
    throw error;
  }
};

// Verify code - returns idpToken directly (5 min expiry, user must act quickly)
// Note: Response no longer includes isNewUser to prevent email enumeration attacks.
// User existence is determined during the login step (loginWithOtp returns signup_required if needed).
export const verifyOtpCode = (email, code) => async dispatch => {
  dispatch(verifyOtpCodeRequest());

  try {
    const response = await apiVerifyOtpCode(email, code);
    // Returns idpToken directly, plus context for resuming chat state
    // Note: isNewUser is no longer returned - user existence is determined during login
    const { idpToken, email: verifiedEmail, context } = response;

    dispatch(
      verifyOtpCodeSuccess({
        idpToken,
        email: verifiedEmail,
        context,
      })
    );
    return { idpToken, email: verifiedEmail, context };
  } catch (error) {
    dispatch(verifyOtpCodeError(storableError(error)));
    throw error;
  }
};

// For existing users: use idpToken directly for login
// Must go through server-side API because sdk.loginWithIdp requires clientSecret
// Returns { signupRequired: true } if user doesn't exist (security: prevents email enumeration)
// Prefer structured fields first, fall back to message substring.
// Also used by OtpForm to pick the message after a failed OTP login.
export const isEmailVerificationError = error => {
  if (!error) return false;
  const errorCode = error.code || error.data?.errors?.[0]?.code;
  const errorStatus = error.status || error.statusCode;
  return (
    errorCode === 'email-unverified' ||
    errorCode === 'email-not-verified' ||
    (errorStatus === 403 && !!error.message?.toLowerCase().includes('verif')) ||
    // Last resort: substring check
    (!!error.message?.includes('email') && !!error.message?.includes('verif'))
  );
};

export const loginWithOtp = idpToken => async (dispatch, getState) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  try {
    const response = await apiLoginWithOtp(idpToken);

    // Check if signup is required (user doesn't exist)
    // Server returns { status: 'signup_required' } for new users
    if (response?.status === 'signup_required') {
      // Reset login state (not an error, just need signup instead)
      dispatch(loginError(null));
      return { signupRequired: true };
    }

    dispatch(loginSuccess());
    await dispatch(fetchCurrentUser());
    dispatch(loginWithIdentitySuccess(getState().user ? getState().user.currentUser : {}, 'otp'));
    return { signupRequired: false };
  } catch (error) {
    // Check if this is an email-not-verified issue
    // Prefer structured fields first, fall back to message substring
    if (isEmailVerificationError(error)) {
      dispatch(
        loginError({
          type: 'email-not-verified',
          message: 'Account exists but email not verified. Please verify your email first.',
        })
      );
    } else {
      dispatch(loginError(storableError(error)));
    }
    throw error;
  }
};

// For new users: use idpToken for create + login (must complete within 5 min of code verification)
// Must go through server-side API because sdk.createWithIdp/loginWithIdp require clientSecret
export const signupWithOtp = (idpToken, userData) => async (dispatch, getState) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());

  const { firstName, lastName, email } = userData;

  // Build attribution data from captured landing page params (from affiliate cookie)
  // Don't invent fake campaigns - auth method ≠ acquisition campaign
  const attribution = {};
  if (affiliate.has(MEDIUM)) attribution.utm_medium = affiliate.get(MEDIUM);
  if (affiliate.has(CAMPAIGN)) attribution.utm_campaign = affiliate.get(CAMPAIGN);
  if (affiliate.has(SOURCE)) attribution.utm_source = affiliate.get(SOURCE);
  if (affiliate.has(AP)) attribution.ap = affiliate.get(AP);
  if (affiliate.has(AID)) attribution.aid = affiliate.get(AID);
  if (affiliate.has(GCLID)) attribution.gclid = affiliate.get(GCLID);
  if (affiliate.has(FBCLID)) attribution.fbclid = affiliate.get(FBCLID);
  if (affiliate.has(GBRAID)) attribution.gbraid = affiliate.get(GBRAID);
  if (affiliate.has(WBRAID)) attribution.wbraid = affiliate.get(WBRAID);

  try {
    await apiSignupWithOtp(idpToken, { email, firstName, lastName, attribution });

    // The server stores signup_auth_method: 'otp' on the user; this tags the event.
    dispatch(signupSuccess(undefined, 'otp'));
    await dispatch(fetchCurrentUser());
    dispatch(loginWithIdentitySuccess(getState().user ? getState().user.currentUser : {}, 'otp'));
    return null;
  } catch (error) {
    const storable = storableError(error);
    dispatch(signupError(storable));
    if (!isKnownSignupError(storable)) {
      log.error(error, 'signup-with-otp-failed', {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
      });
    }
    throw error;
  }
};

export const refreshAuthState = () => async (dispatch, getState) => {
  dispatch(loginSuccess());
  await dispatch(refreshCurrentUser());
  const currentUser = getState().user?.currentUser || {};
  dispatch(loginWithIdentitySuccess(currentUser));
};
